import { Button } from "@mantine/core";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import { isApiError } from "../../../utils/isApiError";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { useMaStRPersonMutation } from "./hooks/useMaStRPersonMutation";
import {
  MASTR_PERSON_DEFAULT_VALUES,
  MASTR_PERSON_FORM_FIELD_DATA,
  type MaStRPersonFormValues
} from "./MaStRSubStepPerson.constants";

interface MaStRSubStepPersonProps {
  variantId: number;
  onSubmit: (taskId: string) => void;
  onBack: () => void;
}

function MaStRSubStepPerson({
  variantId,
  onSubmit,
  onBack
}: MaStRSubStepPersonProps) {
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const mutation = useMaStRPersonMutation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    watch
  } = useForm<MaStRPersonFormValues>({
    defaultValues: MASTR_PERSON_DEFAULT_VALUES
  });

  const onValidSubmit: SubmitHandler<MaStRPersonFormValues> = async (data) => {
    let taskId: string;

    try {
      taskId = await mutation.mutateAsync({
        mastr_nummer: data.mastrNummer,
        variant: variantId
      });
    } catch (error) {
      if (isApiError(error) && error.response?.status === 400) {
        setErrorsFromResponseData<MaStRPersonFormValues>(
          showErrorToast,
          error,
          watch(),
          setError,
          t("errors.UnknownError")
        );
      } else {
        showErrorToast(error);
      }

      return;
    }

    onSubmit(taskId);
  };

  const submitCallback = handleSubmit(onValidSubmit);

  return (
    <div className="mastr-substep">
      <PersonExplanation />
      <form noValidate onSubmit={submitCallback}>
        <FormFieldController
          control={control}
          data={MASTR_PERSON_FORM_FIELD_DATA.mastrNummer}
          error={errors.mastrNummer}
        />
      </form>
      <ModalFooter>
        <Button
          disabled={mutation.isPending}
          variant="default"
          onClick={onBack}
        >
          Zurück
        </Button>
        <Button
          color="secondary"
          loading={mutation.isPending}
          onClick={submitCallback}
        >
          Weiter
        </Button>
      </ModalFooter>
    </div>
  );
}

function PersonExplanation() {
  return (
    <p className="explanation">
      Bitte geben Sie die Marktstammdatenregisters einer juristischen Person mit
      der Marktrolle Anlagenbetreiber ein. Wir haben keinen Zugriff, um
      natürliche Personen über das Marktstammdatenregister abzufragen.
    </p>
  );
}

export { MaStRSubStepPerson, type MaStRSubStepPersonProps };
